// MODULES
import React from "react";
import PropTypes from "prop-types";

// CLASSES
import Session from "@classes/Session";
import Validator from "@classes/Validator";

// COMPONENTS
import DefaultFields from "../../../components/DefaultFields/DefaultFields";
import EditableField from "../../../components/EditableField/EditableField";
import FieldsCollection from "@components/FieldsCollection/FieldsCollection";

// HELPERS
import {createClassName} from "@helpers/utils";
import * as CobHelpers from "../../../Cob.helpers";
import fulfillmentCheck from "./CustomerInformation.fulfillment";


export default class CobCustomerInformation extends React.PureComponent
{
    static propTypes = {
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        session: PropTypes.instanceOf(Session).isRequired,
        sectionNumber: PropTypes.number.isRequired,
        number: PropTypes.number.isRequired,
        prevState: PropTypes.object,
        onChange: PropTypes.func.isRequired
    };

    static companyExclusive = false;
    static privateExclusive = false;
    static fields = _getFields();

    state = _getInitialState.call(this);

    componentDidUpdate(prevProps, prevState)
    {
        const {props, state} = this;
        const {id, onChange} = props;

        if (prevState.fields !== state.fields)
        {
            onChange(id, {...state}, fulfillmentCheck(props, state));
        }
    }

    componentDidMount()
    {
        const {app} = this.props;
        const isFulfilled = fulfillmentCheck(this.props, this.state);
        const fulfilledViewsById = {...app.fulfilledViewsById};
        if (isFulfilled)
        {
            fulfilledViewsById["CustomerInformation"] = true;
        }
        else
        {
            delete fulfilledViewsById["CustomerInformation"];
        }
        this.props.actions.setAppState({fulfilledViewsById});
    }

    render()
    {
        const {props, state} = this;
        const {title, session} = props;
        const classes = createClassName("Cob-CustomerInformation", {
            "Cob-View": true,
            "fulfilled": state.isFulfilled
        });

        let ssnShort = state.fields.customer_social_security_number.replaceAll("-", "");
        if (ssnShort.length > 10)
        {
            ssnShort = ssnShort.substring(2, ssnShort.length);
        }

        return (
            <div className={classes}>
                <DefaultFields fields={this.constructor.fields} />

                <div className="Cob-Fields">
                    <h2>{title}</h2>

                    <FieldsCollection
                        fieldsPerRow={session.isCompany ? 2 : 3}
                        fields={[
                            {
                                render: (
                                    <EditableField
                                        name="customer_social_security_number"
                                        label={session.isCompany ? "Organisationsnummer" : "Personnummer"}
                                        placeholder={session.isCompany ? "XXXXXX-YYYY" : "ÅÅÅÅÅMMDD-XXXX"}
                                        value={state.fields.customer_social_security_number}
                                        readOnly={true}
                                        valid={true}
                                        onChange={this._onFieldChange}
                                        onBlur={this._onFieldBlur}
                                        required
                                    />
                                )
                            },
                            {
                                render: (
                                    <EditableField
                                        name="customer_firstname"
                                        label={session.isCompany ? "Bolag" : "Förnamn"}
                                        value={state.fields.customer_firstname}
                                        valid={!!session.lookupData || state.validFields.customer_firstname}
                                        onChange={this._onFieldChange}
                                        onBlur={this._onFieldBlur}
                                        required
                                    />
                                )
                            },
                            {
                                render: (
                                    <EditableField
                                        name="customer_lastname"
                                        label="Efternamn"
                                        value={state.fields.customer_lastname}
                                        valid={!!session.lookupData || state.validFields.customer_lastname}
                                        onChange={this._onFieldChange}
                                        onBlur={this._onFieldBlur}
                                        required
                                    />
                                ),
                                display: !session.isCompany
                            }
                        ]}
                    />

                    {!session.isCompany && (
                        <React.Fragment>
                            <p style={{margin: "20px 0 10px 0"}}>
                                Det digitala avtalet kommer att skickas ut för signering till e-postadressen du anger nedan.
                            </p>

                            <FieldsCollection
                                fieldsPerRow={2}
                                fields={[
                                    {
                                        render: (
                                            <EditableField
                                                type="email"
                                                name="customer_email"
                                                label="E-postadress"
                                                value={state.fields.customer_email}
                                                valid={state.validFields.customer_email}
                                                onChange={this._onFieldChange}
                                                onBlur={this._onFieldBlur}
                                                required
                                            />
                                        )
                                    }
                                ]}
                            />
                        </React.Fragment>
                    )}

                    <input type="hidden"
                           name="customer_social_security_number"
                           value={ssnShort} />

                    <input type="hidden" name="customer_name"
                           value={state.fields.customer_firstname + (session.isCompany ? "" : " " + state.fields.customer_lastname)}/>

                </div>
            </div>
        );
    }

    // Internal methods
    _fieldValidator = (name, value) =>
    {
        if (name === "customer_social_security_number")
        {
            return this.props.session.isCompany ? Validator.validateOrgNum(value, true) : Validator.validateSsn(value, true);
        }
        else if (name === "customer_email")
        {
            return Validator.validateEmail(value);
        }
        else
        {
            return value.length > 0;
        }
    };

    _onFieldChange = (name, value) =>
    {
        CobHelpers.onViewFieldChange.call(this, name, value);
    };

    _onFieldBlur = (name) =>
    {
        CobHelpers.onViewFieldBlur.call(this, name);
    };
}


// PRIVATE FUNCTIONS
function _getFields()
{
    return [
        "customer_name",
        "customer_firstname",
        "customer_lastname",
        "customer_social_security_number",
        "customer_email"
    ];
}

function _getInitialState()
{
    if (this.props.prevState)
    {
        return {...this.props.prevState};
    }

    const {session} = this.props;
    const {isCompany} = session;
    const getLookupData = (name) => session.lookupData ? session.lookupData[name] || "" : "";

    return {
        validFields: !session.lookupData ? {} : {
            "is_company": true,
            "customer_firstname": !!getLookupData(isCompany ? "companyName" : "firstName"),
            "customer_social_security_number": !!getLookupData(isCompany ? "orgNumber" : "ssn"),
            "customer_email": isCompany ? true : !!getLookupData("email"),
            ...(
                !isCompany ? {
                    "customer_lastname": !!getLookupData("lastName")
                } : undefined
            )
        },
        fields: {
            "is_company": isCompany,
            "customer_firstname": getLookupData(isCompany ? "companyName" : "firstName"),
            "customer_lastname": !isCompany ? getLookupData("lastName") : "",
            "customer_social_security_number": getLookupData(isCompany ? "orgNumber" : "ssn"),
            "customer_email": !isCompany ? getLookupData("email") : "",
        },
    };
}


// WEBPACK FOOTER //
// ./src/routes/Cob/views/CustomerInformation/simple/CustomerInformation.js